import { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import {
  XMarkIcon,
  ArrowUpIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
  UsersIcon,
  ArrowDownRightIcon,
  ArrowUpRightIcon,
  BellSlashIcon,
} from "@heroicons/react/24/outline";
import moment from "moment";
import { loadLastActivityPaginate } from "../../../utils/lastActivity/lastActivity.util";
import { validaLargoPayment } from "../../../utils/validateData";
import { SkeletonDahsboard } from "../../skeleton/skeletonDash";
import { environment } from "../../../configs/variables";

function getCurrentDimension() {
  return {
    width: window.innerWidth,
    height: window.innerHeight,
  };
}

export function DisclosureLastActivity({ device }) {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [limit, setLimit] = useState(8);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [showSkeleton, setshowSkeleton] = useState(false);
  const [movements, setMovements] = useState([]);
  const [screenSize, setScreenSize] = useState(getCurrentDimension());

  const handleLoadLastMovements = async () => {
    setOpen(true);
    setIsLoading(true);
    setshowSkeleton(true);
    loadLastActivityPaginate({
      filter: { documento: localStorage.getItem("doc") },
      limit: limit,
      page: page,
    })
      .then((activities) => {
        setPage((prevPage) => prevPage + 1);
        setMovements(activities);
        setIsLoading(false);
        setshowSkeleton(false);
      })
      .catch((e) => console.log(e));
  };

  const handleResizeLimit = useCallback((_limit) => {
    setLimit(_limit);
    return;
  }, []);

  const handleResize = useCallback(() => {
    const height = screenSize.height;
    const width = screenSize.width;
    /* console.log(`Height: ${height} - Width: ${width}`); */
    if (height >= 700 && height <= 950 && width >= 374 && width <= 500) {
      //telefonos medianos y grandes
      if (environment !== "PROD") console.log("Size large phone range");
      handleResizeLimit(10);
    } else if (height >= 550 && height <= 699 && width >= 374 && width <= 500) {
      // telefonos pequeños
      if (environment !== "PROD") console.log("Size small phone range");
      handleResizeLimit(7);
    } else if (
      height >= 951 &&
      height <= 1366 &&
      width >= 760 &&
      width <= 1024
    ) {
      //tablets hasta el ipad pro
      if (environment !== "PROD") console.log("Size tablet range");
      handleResizeLimit(17);
    } else if (height >= 695 && width >= 1530) {
      if (environment !== "PROD") console.log("Size Desktop mini range");
      handleResizeLimit(8);
    } else if (height >= 910 && width >= 1920) {
      if (environment !== "PROD") console.log("Size Desktop big range");
      handleResizeLimit(15);
    } else {
      handleResizeLimit(8); // Mostrar 9 elementos para desktop
    }
  }, [screenSize, handleResizeLimit]);

  const handleLoadMoreMovements = useCallback(async () => {
    if (isLoading) return;
    if (!hasMore) return;

    setIsLoading(true);
    await loadLastActivityPaginate({
      filter: { documento: localStorage.getItem("doc") },
      limit: limit,
      page: page,
    })
      .then((activities) => {
        if (Array.isArray(activities) && activities.length > 0) {
          activities.forEach((a) => {
            setMovements((prevMov) => [...prevMov, a]);
          });
          setHasMore(true);
          setPage((prevPage) => prevPage + 1);
        } else {
          setHasMore(false);
        }
        setIsLoading(false);
      })
      .catch((e) => console.log(e));
  }, [page, isLoading, hasMore, limit]);

  const determineOperator = (name = "", payValue) => {
    let payFormat = validaLargoPayment(payValue);
    let USDollar = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    });
    let operator = "";
    if (name === "Firmaste los documentos") {
      operator = <span>+{USDollar.format(payFormat).split(".")[0]} </span>;
    } else {
      operator = <span>-{USDollar.format(payFormat).split(".")[0]}</span>;
    }
    return operator;
  };

  const determineIconMovements = (name = "") => {
    let html = "";
    if (name === "Firmaste los documentos") {
      html = (
        <div className="mr-2 grid content-center justify-center w-8 h-8 bg-greenLight rounded-lg font-bold">
          <ArrowDownRightIcon className="w-4 h-4 text-green font-bold" />
        </div>
      );
    } else {
      html = (
        <div className="mr-2 grid content-center justify-center w-8 h-8 bg-redBgLight rounded-lg font-bold">
          <ArrowUpRightIcon className="w-4 h-4 text-red font-bold" />
        </div>
      );
    }
    return html;
  };

  const handleClose = () => {
    setOpen(false);
    setPage(1);
    setHasMore(true);
    setshowSkeleton(false);
    setIsLoading(false);
    setLimit(9);
  };

  const mapSkeleton = [
    {
      height: 80,
      width: 350,
    },
    {
      height: 80,
      width: 350,
    },
    {
      height: 80,
      width: 350,
    },
    {
      height: 80,
      width: 350,
    },
    {
      height: 80,
      width: 350,
    },
  ];

  useEffect(() => {
    const updateDimension = () => {
      setScreenSize(getCurrentDimension());
    };
    window.addEventListener("resize", updateDimension);

    return () => {
      window.removeEventListener("resize", updateDimension);
    };
  }, [screenSize]);

  useEffect(() => {
    handleResize();
  }, [handleResize]);

  return (
    <>
      {device !== "desktop" ? (
        <div
          className={"w-full"}
          onClick={() => handleLoadLastMovements()}
          href="#"
        >
          <div className="cursor-pointer min-w-full">
            <div className="flex justify-between">
              <div className="flex gap-2 py-1">
                <div className="w-5 h-auto">
                  <UsersIcon />{" "}
                </div>
                <div>Movimientos</div>
              </div>
              <div className="grid content-center">
                <ChevronRightIcon className="w-5 h-auto" />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="flex justify-between mt-1">
            <h1 className="font-bold text-lg">Movimientos</h1>
            <button
              type="button"
              onClick={() => handleLoadLastMovements()}
              className="grid content-center text-sm text-grayP"
              style={{ cursor: "pointer" }}
            >
              Ver todo
            </button>
          </div>
        </>
      )}

      <Dialog open={open} onClose={handleClose} className="relative z-50">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-transparent xl:bg-darkBgLight/75 transition-opacity duration-200 ease-in-out data-closed:opacity-0"
        />

        <div className="fixed inset-x-0">
          <div className="absolute inset-0">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full md:pl-10">
              <DialogPanel
                transition
                className="pointer-events-auto w-screen md:max-w-sm transform transition duration-300 ease-in-out data-closed:translate-x-full"
              >
                <div className="flex h-full flex-col bg-darkBg tracking-tight font-walsheim">
                  <div>
                    <div className="py-6 bg-darkBg">
                      <div className="flex justify-between px-4 pt-8 ">
                        <div className="flex h-7 items-center">
                          <button
                            type="button"
                            onClick={() => handleClose()}
                            className="relative text-white focus:outline-hidden"
                            style={{ cursor: "pointer" }}
                          >
                            <span className="absolute" />
                            <span className="sr-only">Close panel</span>
                            <ArrowLeftIcon
                              aria-hidden="true"
                              className="h-6 w-6"
                            />
                          </button>
                        </div>
                        <DialogTitle>
                          <div className="text-white">Movimientos</div>
                        </DialogTitle>
                        <div className="ml-3 flex h-7 items-center">
                          <button
                            type="button"
                            onClick={() => handleClose()}
                            className="relative text-white focus:outline-hidden"
                            style={{ cursor: "pointer" }}
                          >
                            <span className="absolute" />
                            <span className="sr-only">Close panel</span>
                            <XMarkIcon aria-hidden="true" className="h-6 w-6" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Cuerpo del dialogo de movimientos */}

                  <div className="h-full overflow-y-auto rounded-t-3xl bg-white">
                    <div className="p-4">
                      {showSkeleton ? (
                        <>
                          {mapSkeleton.map((skeleton, idx) => {
                            return (
                              <div
                                key={`skeleton-${idx}`}
                                className="flex justify-between text-sm font-bold w-full pb-4"
                              >
                                <div className="flex gap-2">
                                  <div className="h-full grid content-start">
                                    <SkeletonDahsboard height={50} width={30} />
                                  </div>
                                  <div className="block">
                                    <div>
                                      <SkeletonDahsboard
                                        height={20}
                                        width={150}
                                      />
                                    </div>
                                    <div className="text-grayP text-sm font-normal">
                                      <span>
                                        <SkeletonDahsboard
                                          height={20}
                                          width={50}
                                        />
                                      </span>
                                    </div>
                                  </div>
                                </div>

                                <SkeletonDahsboard height={20} width={80} />
                              </div>
                            );
                          })}
                        </>
                      ) : (
                        (movements ?? []).map((activity, index) => {
                          return (
                            <div
                              key={activity?.id ?? `movement-${index}`}
                              className="flex justify-between text-sm font-bold w-full pb-4"
                            >
                              <div className="flex" key={index - 1}>
                                <div className="h-full grid content-center">
                                  {determineIconMovements(
                                    activity?.activity?.name,
                                  )}
                                </div>
                                <div className="block">
                                  <div>{activity?.activity?.name ?? ""}</div>
                                  <div className="text-grayP text-sm font-normal">
                                    <span>
                                      {moment(
                                        activity?.activity?.activityDate,
                                        "DD/MM/YYYY HH:mm:ss",
                                      ).format("DD/MM/YYYY")}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {determineOperator(
                                activity?.activity?.name,
                                activity?.activity?.payValue,
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>

                  <div
                    onClick={handleLoadMoreMovements}
                    className="text-grayP text-sm flex justify-center bg-white py-2"
                  >
                    {isLoading ? (
                      <div>
                        <div className="flex justify-center">
                          <div className="loader-clousure-movements"> </div>
                        </div>
                        Cargando movimientos...
                      </div>
                    ) : hasMore === false ? (
                      <div style={{ height: "4.375rem" }}>
                        <div className="flex justify-center">
                          <BellSlashIcon
                            className="w-4 h-auto"
                            style={{ height: "2.5rem" }}
                          />
                        </div>
                        No hay más movimientos
                      </div>
                    ) : (
                      <div
                        className="hover:cursor-pointer"
                        style={{ height: "4.375rem" }}
                      >
                        <div className="flex justify-center">
                          <ArrowUpIcon
                            className="w-4 h-auto"
                            style={{ height: "2.5rem" }}
                          />
                        </div>
                        Cargar movimientos
                      </div>
                    )}
                  </div>
                </div>
              </DialogPanel>
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
}
